/**
 * Makes every settings <section> independently collapsible: its .section-title
 * becomes a button that hides/shows the rest of the section. Collapsed state is
 * remembered per section across reloads.
 */

const STORAGE_KEY = 'webSynth.collapsed.v1';

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function save(ids: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

export function installCollapsibles(root: ParentNode): void {
  const collapsed = loadCollapsed();

  for (const title of root.querySelectorAll<HTMLElement>('section > .section-title')) {
    const section = title.closest('section');
    if (!section) continue;
    const id = section.getAttribute('aria-labelledby') ?? title.id;

    title.classList.add('collapsible');
    title.setAttribute('role', 'button');
    title.setAttribute('tabindex', '0');

    const apply = (isCollapsed: boolean): void => {
      section.classList.toggle('collapsed', isCollapsed);
      title.setAttribute('aria-expanded', String(!isCollapsed));
    };
    apply(collapsed.has(id));

    const toggle = (): void => {
      const next = !section.classList.contains('collapsed');
      apply(next);
      if (next) collapsed.add(id);
      else collapsed.delete(id);
      save(collapsed);
    };

    title.addEventListener('click', toggle);
    title.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        toggle();
      }
    });
  }
}
